/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Attachment editor for existing payment reconciliations
 */
import { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { Button, Flex, Spin, Tag, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { DeleteOne, Upload as UploadIcon } from '@icon-park/react';

import WidgetButton from 'components/widgets/button';
import useGetAllAttachments from 'hooks/useGetAllAttachments';
import AppEvents, { Events } from '@veritable/common/utils/appEvents';
import { FHIR_ENTITY_TYPES, S3_BLOB_CATEGORY } from '@veritable/common/constants';
import { normalizeAcceptedExtensions, partitionAttachmentFiles } from 'utils/uploadAttachment';

const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'rtf', 'txt', 'xls', 'csv', 'jpg', 'png', 'tiff'];

const attachmentRowClassName = 'rounded-md border border-solid border-neutral-4 bg-neutral-1 px-3 py-2';

interface IProps {
	entityId: string;
	pendingUploadFiles: File[];
	onPendingUploadFilesChange: (files: File[]) => void;
	pendingDeleteBlobIds: string[];
	onPendingDeleteBlobIdsChange: (blobIds: string[]) => void;
	disabled?: boolean;
}

const PaymentAttachmentEditor: React.FC<IProps> = ({
	entityId,
	pendingUploadFiles,
	onPendingUploadFilesChange,
	pendingDeleteBlobIds,
	onPendingDeleteBlobIdsChange,
	disabled,
}) => {
	const {
		data: attachments = [],
		isLoading,
		isFetching,
	} = useGetAllAttachments({
		id: entityId,
		entityType: FHIR_ENTITY_TYPES.paymentReconciliation,
	});
	const acceptedExtensions = useMemo(() => normalizeAcceptedExtensions(ACCEPTED_EXTENSIONS), []);
	const isLoadingAttachments = isLoading || isFetching;

	// Uploads are staged as drafts and only committed when the user saves, so that Cancel/Discard
	// truly reverses them (nothing is persisted server-side until save).
	const handleBeforeUpload = useCallback(
		(file: RcFile, fileList: RcFile[]) => {
			if (file === fileList[0]) {
				const { validFiles, errorMessages } = partitionAttachmentFiles(fileList, acceptedExtensions, Number.POSITIVE_INFINITY);
				_.forEach(errorMessages, (message) => AppEvents.publish(Events.Error, message));
				if (!_.isEmpty(validFiles)) {
					onPendingUploadFilesChange([...pendingUploadFiles, ...validFiles]);
				}
			}
			return Upload.LIST_IGNORE;
		},
		[acceptedExtensions, onPendingUploadFilesChange, pendingUploadFiles],
	);

	const removePendingUpload = useCallback(
		(index: number) => onPendingUploadFilesChange(_.filter(pendingUploadFiles, (_file, fileIndex) => fileIndex !== index)),
		[onPendingUploadFilesChange, pendingUploadFiles],
	);

	const markForDelete = useCallback(
		(blobId: string) => onPendingDeleteBlobIdsChange(_.uniq([...pendingDeleteBlobIds, blobId])),
		[onPendingDeleteBlobIdsChange, pendingDeleteBlobIds],
	);

	const undoDelete = useCallback(
		(blobId: string) => onPendingDeleteBlobIdsChange(_.without(pendingDeleteBlobIds, blobId)),
		[onPendingDeleteBlobIdsChange, pendingDeleteBlobIds],
	);

	const hasNoAttachments = _.isEmpty(attachments) && _.isEmpty(pendingUploadFiles);

	return (
		<div className='px-6 pb-16'>
			<Flex align='center' justify='space-between' className='mb-2'>
				<Flex vertical>
					<Typography.Text strong>{'Upload Attachment(s)'}</Typography.Text>
					<Typography.Text type='secondary' className='text-xs'>
						{'Allowed file formats: pdf, doc, docx, rtf, txt, xls, csv, jpg, png, tiff'}
					</Typography.Text>
				</Flex>
				<Upload
					multiple
					showUploadList={false}
					accept={acceptedExtensions.accept}
					beforeUpload={handleBeforeUpload}
					disabled={disabled}
				>
					<Button type='default' icon={<UploadIcon />} disabled={disabled}>
						{'Choose Files'}
					</Button>
				</Upload>
			</Flex>

			<Spin spinning={isLoadingAttachments}>
				<Flex vertical gap={8}>
					{_.map(attachments, (attachment) => {
						const isMarkedForDelete = _.includes(pendingDeleteBlobIds, attachment.blobId);
						return (
							<Flex key={attachment.blobId} align='center' justify='space-between' gap={8} className={attachmentRowClassName}>
								<Flex vertical className='min-w-0'>
									<Typography.Text ellipsis delete={isMarkedForDelete}>
										{attachment.fileName || 'Unknown File'}
									</Typography.Text>
									<Typography.Text type='secondary' className='text-xs'>
										{attachment.fileType || 'Unknown type'}
									</Typography.Text>
								</Flex>
								<Flex align='center' gap={8}>
									{isMarkedForDelete ? (
										<>
											<Tag color='warning'>{'Will delete'}</Tag>
											<Button
												type='link'
												size='small'
												onClick={() => undoDelete(attachment.blobId)}
												disabled={disabled}
											>
												{'Undo'}
											</Button>
										</>
									) : (
										<>
											<WidgetButton.DownloadBlob
												blobCategory={S3_BLOB_CATEGORY.entityattachment}
												blobId={attachment.blobId}
												entityType={FHIR_ENTITY_TYPES.paymentReconciliation}
												contentType={attachment.fileType}
												title={attachment.fileName}
												hideTitle
												wrapperClassName='mr-1 shrink-0'
												buttonProps={{ className: 'h-8 min-w-8 shrink-0' }}
											/>
											<Button
												danger
												icon={<DeleteOne />}
												onClick={() => markForDelete(attachment.blobId)}
												disabled={disabled}
											/>
										</>
									)}
								</Flex>
							</Flex>
						);
					})}

					{_.map(pendingUploadFiles, (file, index) => (
						<Flex
							key={`pending-upload-${index}-${file.name}`}
							align='center'
							justify='space-between'
							gap={8}
							className={attachmentRowClassName}
						>
							<Flex vertical className='min-w-0'>
								<Typography.Text ellipsis>{file.name || 'Unknown File'}</Typography.Text>
								<Typography.Text type='secondary' className='text-xs'>
									{file.type || 'Unknown type'}
								</Typography.Text>
							</Flex>
							<Flex align='center' gap={8}>
								<Tag color='processing'>{'Will upload'}</Tag>
								<Button type='link' size='small' onClick={() => removePendingUpload(index)} disabled={disabled}>
									{'Remove'}
								</Button>
							</Flex>
						</Flex>
					))}

					{hasNoAttachments && !isLoadingAttachments && (
						<Typography.Text type='secondary' className='py-3 text-center'>
							{'No attachments selected'}
						</Typography.Text>
					)}
				</Flex>
			</Spin>
		</div>
	);
};

export default PaymentAttachmentEditor;
